import React, { useEffect, useReducer } from "react";
import moment from "moment";
import {
  Form,
  Modal,
  Radio,
  Input,
  Space,
  Select,
  Col,
  Row,
  DatePicker,
  Avatar,
  Badge,
  Upload,
  Spin,
  notification,
} from "antd";
import { UserOutlined, PaperClipOutlined } from "@ant-design/icons";
import AppTheme from "../../../theme/AppTheme";
import { SuccessButton, DarkButton } from "../../../components";
import styled from "@xstyled/styled-components";
import { ModalProps } from "antd/lib/modal";
import { Store } from "antd/lib/form/interface";
import { IFamilyTreePerson, Gender } from "../../../interfaces";
import { Backend, containsKeys } from "../../../helpers";
import axios, { AxiosError, AxiosResponse, CancelTokenSource } from "axios";
import { RcFile } from "antd/lib/upload";
import maleImg from "../../../assets/img/male.png";
import femaleImg from "../../../assets/img/female.png";

const { Option } = Select;

const AvatarIcon = styled(UserOutlined)`
  font-size: 8rem;
`;

type AddMemberProps = {
  visible: boolean;
  initialValue?: IFamilyTreePerson;
  suggestion?: {
    gender: Gender;
  };
  title?: string;
  handleSubmit?: (values: Store) => void;
};

export declare type AddMemberState = {
  savedPersons: IFamilyTreePerson[];
  peopleFound: IFamilyTreePerson[];
  searchingPerson: boolean;
  modalVisible: boolean;
  loading: boolean;
  searchPersonTS: CancelTokenSource;
  changedValues: Store;
  profilePicture?: string;
};

const AddMember: React.FC<ModalProps & AddMemberProps> = (props) => {
  // extract used props
  const {
    initialValue,
    handleSubmit,
    suggestion,
    maskClosable,
    className,
    width,
    visible,
    title,
    centered,
    footer,
    bodyStyle,
    ...propsRest
  } = props;

  /**
   * States management
   */
  const reducer = (
    state: AddMemberState,
    action: {
      type: "updateState";
      state?: Partial<AddMemberState>;
    }
  ): AddMemberState => {
    switch (action.type) {
      case "updateState":
        return {
          savedPersons: action.state?.savedPersons ?? state.savedPersons,
          modalVisible: action.state?.modalVisible ?? state.modalVisible,
          loading: action.state?.loading ?? state.loading,
          peopleFound: action.state?.peopleFound ?? state.peopleFound,
          searchingPerson:
            action.state?.searchingPerson ?? state.searchingPerson,
          searchPersonTS: action.state?.searchPersonTS ?? state.searchPersonTS,
          changedValues: action.state?.changedValues ?? state.changedValues,
          profilePicture: action.state?.profilePicture ?? state.profilePicture,
        };

      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, {
    savedPersons: [],
    modalVisible: visible,
    loading: false,
    peopleFound: [],
    searchingPerson: false,
    searchPersonTS: axios.CancelToken.source(),
    changedValues: {},
  });

  /**
   * Update modal state
   */
  useEffect(() => {
    dispatch({ type: "updateState", state: { modalVisible: visible } });
  }, [visible]);

  /**
   * Cancel pending token
   */
  useEffect(() => {
    return () => {
      state.searchPersonTS.cancel();
    };
  }, [state.searchPersonTS]);

  const onFinish = (values: Store) => {
    // send data to parent component
    if (handleSubmit) handleSubmit(values);
  };

  const valuesChanged = (changedValues: Store, values: Store) => {
    // update state
    dispatch({ type: "updateState", state: { changedValues: changedValues } });

    /**
     * Search person in database if watched keys changed
     */
    if (
      containsKeys(changedValues, ["first_name", "last_name", "birthplace"])
    ) {
      // search potential existing person
      searchPerson(values);
    }
  };

  /**
   * Search person
   */
  const searchPerson = (values: Store) => {
    // cancel previous request
    state.searchPersonTS.cancel();

    // request params
    const params: any = {};
    // set request attributes
    if (values.first_name) params.firstname = values.first_name;
    if (values.last_name) params.lastname = values.last_name;
    if (values.birthplace) params.birthplace = values.birthplace;

    const tokenSource = axios.CancelToken.source();

    // update searching state
    dispatch({
      type: "updateState",
      state: {
        searchingPerson: true,
        searchPersonTS: tokenSource,
      },
    });

    // search person
    Backend.getInstance()
      .axios()
      .get("/search/find", {
        params,
        cancelToken: tokenSource.token,
      })
      .then((response: AxiosResponse<IFamilyTreePerson[]>) => {
        // update searching state
        dispatch({
          type: "updateState",
          state: { peopleFound: response.data },
        });
      })
      .catch((reason: AxiosError<any>) => {
        if (!axios.isCancel(reason)) {
          notification.warn({
            message:
              "Search failed using the information provided. Try again later please.",
          });
        }
      })
      .finally(() => {
        // update searching state
        dispatch({ type: "updateState", state: { searchingPerson: false } });
      });
  };

  function getBase64(img: RcFile, callback: (image: any) => void) {
    const reader = new FileReader();
    reader.addEventListener("load", () => callback(reader.result));
    reader.readAsDataURL(img);
  }

  function getDefaultPicture() {
    if (state.profilePicture) {
      return state.profilePicture;
    } else {
      const gender = suggestion?.gender ?? initialValue?.gender;
      if (gender) {
        switch (gender) {
          case "male":
            return maleImg;
          case "female":
            return femaleImg;
          default:
            return undefined;
        }
      }
    }
  }

  const initialValuePartial = (): Partial<IFamilyTreePerson> => {
    if (suggestion) {
      return {
        gender: ["male", "female"].includes(suggestion.gender)
          ? (suggestion.gender as any)
          : undefined,
      };
    } else {
      if (initialValue) {
        const { dob, ...rest } = initialValue;
        return {
          dob: moment(dob, "YYYY-MM-DD"),
          ...rest,
        };
      } else {
        return {};
      }
    }
  };

  return (
    <Modal
      maskClosable={maskClosable ?? false}
      className="custom-ant-modal-content"
      width="740px"
      visible={state.modalVisible}
      title={title ?? "Add Member"}
      centered={true}
      footer={null}
      bodyStyle={
        bodyStyle ?? {
          borderTop: "0px",
          backgroundColor: AppTheme.colors.modalBackground,
        }
      }
      {...propsRest}
    >
      <Spin spinning={state.modalVisible && state.loading} size="large">
        <Form
          className="ant-form-extend"
          layout="vertical"
          initialValues={{ layout: "vertical", ...initialValuePartial() }}
          onValuesChange={valuesChanged}
          scrollToFirstError={true}
          onFinish={onFinish}
        >
          <Row>
            <Col xs={24} sm={18}>
              <Row>
                <Col xs={24} sm={8}>
                  <Form.Item
                    className="rounded-md"
                    name="title"
                    label="Title"
                    hasFeedback
                    rules={[
                      {
                        required: true,
                        message: "The title is required",
                      },
                    ]}
                  >
                    <Select
                      className="rounded-md"
                      placeholder="Select a title"
                      allowClear
                    >
                      <Option value="male">M.</Option>
                      <Option value="female">Mme</Option>
                      <Option value="other">Other</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={16}>
                  <Form.Item
                    label="First name"
                    hasFeedback
                    validateStatus={
                      containsKeys(state.changedValues, ["first_name"]) &&
                      state.searchingPerson
                        ? "validating"
                        : undefined
                    }
                    name="first_name"
                    rules={[
                      {
                        required: true,
                        message: "The first name is required!",
                      },
                    ]}
                  >
                    <Input
                      type="text"
                      className="rounded-md"
                      placeholder="First name"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col flex="auto">
                  <Form.Item label="Middle name" name="middle_name" hasFeedback>
                    <Input
                      type="text"
                      className="rounded-md"
                      placeholder="Middle name"
                    />
                  </Form.Item>
                </Col>
                <Col flex="auto">
                  <Form.Item
                    label="Last name"
                    name="last_name"
                    hasFeedback
                    validateStatus={
                      containsKeys(state.changedValues, ["last_name"]) &&
                      state.searchingPerson
                        ? "validating"
                        : undefined
                    }
                    rules={[
                      {
                        required: true,
                        message: "The last name is required",
                      },
                    ]}
                  >
                    <Input
                      type="text"
                      className="rounded-md"
                      placeholder="Last name"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item
                name="picture"
                label="Picture"
                valuePropName="fileList"
                hasFeedback
              >
                <Upload
                  name="logo"
                  beforeUpload={(file: RcFile, FileList: RcFile[]) => {
                    getBase64(file, (imageBase64: string | undefined) => {
                      dispatch({
                        type: "updateState",
                        state: { profilePicture: imageBase64 },
                      });
                    });
                    return false;
                  }}
                  multiple={false}
                  showUploadList={false}
                  accept="image/*"
                >
                  <Badge
                    offset={[-10, 25]}
                    count={
                      <span
                        style={{ backgroundColor: AppTheme.colors.success }}
                        className="rounded-full cursor-pointer p-1 text-white"
                      >
                        <PaperClipOutlined />
                      </span>
                    }
                  >
                    <Avatar
                      className="cursor-pointer w-32 h-32"
                      size={"large"}
                      src={getDefaultPicture()}
                      icon={<AvatarIcon />}
                    />
                  </Badge>
                </Upload>
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col xs={24} md={8}>
              <Form.Item label="Living in" name="living-in" hasFeedback>
                <Input
                  type="text"
                  className="rounded-md"
                  placeholder="Living in"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label="Local govt area"
                name="local-govt-area"
                hasFeedback
              >
                <Input
                  type="text"
                  className="rounded-md"
                  placeholder="Local govt area"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Email" name="email" hasFeedback>
                <Input
                  type="email"
                  className="rounded-md"
                  placeholder="Email"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col flex="auto">
              <Form.Item
                label="Gender"
                name="gender"
                rules={[
                  {
                    required: true,
                    message: "The gender is required",
                  },
                ]}
              >
                <Radio.Group
                  disabled={["male", "female"].includes(
                    suggestion?.gender ?? ""
                  )}
                  size="middle"
                  name="gender"
                >
                  <Radio value="male">Male</Radio>
                  <Radio value="female">Female</Radio>
                  <Radio value="unknown">Unknown</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col flex="auto">
              <Form.Item
                label="Status"
                name="status"
                rules={[{ required: true, message: "The status is required" }]}
              >
                <Radio.Group size="middle" name="status">
                  <Radio value="deceaded">Deceaded</Radio>
                  <Radio value="living">Living</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col flex="auto">
              <Form.Item
                name="dob"
                label="Birthdate"
                hasFeedback
                rules={[{ required: true, message: "Birthdate is required" }]}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  className="rounded-md w-full"
                  placeholder="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
            <Col flex="auto">
              <Form.Item
                label="Birthplace"
                hasFeedback
                validateStatus={
                  containsKeys(state.changedValues, ["birthplace"]) &&
                  state.searchingPerson
                    ? "validating"
                    : undefined
                }
                name="birthplace"
              >
                <Input
                  type="text"
                  className="rounded-md"
                  placeholder="City, State, Country"
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item>
            <Space>
              <SuccessButton
                htmlType="submit"
                className="rounded-md uppercase text-sm"
              >
                Continue
              </SuccessButton>
              <DarkButton
                onClick={props.onCancel}
                className="rounded-md uppercase text-sm"
                type="default"
              >
                Cancel
              </DarkButton>
            </Space>
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default AddMember;
